import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InfoModal } from "@newsela/angelou";
import { useRouter } from "next/router";

import { ContentLevel } from "@/types/models/ContentLevel.interface";

import {
  CitationFormInputKey,
  ICitationFormInput,
  IContentData,
} from "../CitationAction.interface";
import CitationEditForm from "../CitationEditForm/CitationEditForm";
import CitationGeneratorBody from "../CitationGenerator/CitationGeneratorBody";
import { setUpCitationState } from "../utils";
import {
  modalCss,
  modalFooterCss,
  modalContentWrapperCss,
} from "./CitationModal.style";
import CitationModalFooter from "./CitationModalFooter/CitationModalFooter";

interface CitationModalProps {
  setActive: (active: boolean) => void;
  currentLevel: ContentLevel | undefined;
  contentData: IContentData;
  modalProps: any;
}

const CitationModal = ({
  setActive,
  contentData,
  currentLevel,
  modalProps,
}: CitationModalProps) => {
  const router = useRouter();
  const [showEditForm, setShowEditForm] = useState(false);
  const submitFormRef = useRef<(() => void) | null>(null);

  const handleSubmitReady = useCallback((submitFn: () => void) => {
    submitFormRef.current = submitFn;
  }, []);
  const lastPublication = useMemo(
    () => contentData?.article_publications?.slice(-1)[0]?.visible_after,
    [contentData?.article_publications],
  );

  const [citationData, setCitationData] = useState(
    setUpCitationState(contentData, currentLevel, router, lastPublication),
  );

  useEffect(() => {
    setCitationData(
      setUpCitationState(contentData, currentLevel, router, lastPublication),
    );
  }, [contentData, currentLevel, router.asPath, lastPublication]);

  const updateCitationData = (key: CitationFormInputKey, value: string) => {
    setCitationData((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditChanges = (data: ICitationFormInput) => {
    Object.entries(data).map(([key, value]) =>
      updateCitationData(key as CitationFormInputKey, value),
    );
  };

  return (
    <InfoModal
      labelId="citation-generator"
      title="Citation generator"
      onBackgroundClick={() => setActive(false)}
      __cssFor={{
        root: modalCss,
        modalContentWrapper: modalContentWrapperCss,
        footer: modalFooterCss,
      }}
      footer={
        <CitationModalFooter
          showEditForm={showEditForm}
          setShowEditForm={setShowEditForm}
          setActive={setActive}
          onSaveEdits={() => submitFormRef.current?.()}
        />
      }
      {...modalProps}
    >
      {showEditForm ? (
        <CitationEditForm
          setShowEditForm={setShowEditForm}
          handleEditChanges={handleEditChanges}
          citationData={citationData}
          onSubmit={handleSubmitReady}
        />
      ) : (
        <CitationGeneratorBody
          setShowEditForm={setShowEditForm}
          citationData={citationData}
        />
      )}
    </InfoModal>
  );
};

export default CitationModal;
